import { IDEMPOTENCY_OUTCOME_UNCERTAIN_CODE, RivetsApiError, WORKER_OUTCOME_UNKNOWN_CODE } from "@rivets/core";

export interface IntentRequestIdentity {
  intent: string;
  id: string;
}

export interface RequestIdentityStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface RequestIdentityPersistence {
  storage: RequestIdentityStorage;
  key: string;
}

export function requestIdForIntent(
  current: IntentRequestIdentity | undefined,
  intent: string,
): IntentRequestIdentity {
  return current?.intent === intent ? current : { intent, id: crypto.randomUUID() };
}

/**
 * Owns request identity for consequential actions. An identity survives only
 * when the browser cannot tell whether the server received the request. A
 * successful or definitive response finishes that attempt; the next click is
 * a new user action and receives a new identity.
 *
 * One controller also serializes the Git controls in a workspace. This closes
 * the small gap before React renders a mutation's pending state, during which
 * a double click could otherwise dispatch the same operation twice.
 */
export class RequestLifecycleController {
  private readonly identities = new Map<string, string>();
  private active: IntentRequestIdentity | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly createId: () => string = () => crypto.randomUUID(),
    private readonly persistence?: RequestIdentityPersistence,
  ) {
    this.restore();
  }

  start(intent: string): IntentRequestIdentity | null {
    if (this.active) return null;
    const request = { intent, id: this.identities.get(intent) ?? this.createId() };
    this.identities.set(intent, request.id);
    this.persist();
    this.active = request;
    this.emit();
    return request;
  }

  settle(request: IntentRequestIdentity, error?: unknown): void {
    if (error === undefined || !shouldRetainRequestIdentity(error)) {
      if (this.identities.get(request.intent) === request.id) {
        this.identities.delete(request.intent);
        this.persist();
      }
    }
    if (this.active?.intent === request.intent && this.active.id === request.id) {
      this.active = null;
      this.emit();
    }
  }

  isBusy = (): boolean => this.active !== null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private restore(): void {
    if (!this.persistence) return;
    try {
      const raw = this.persistence.storage.getItem(this.persistence.key);
      if (!raw) return;
      const stored: unknown = JSON.parse(raw);
      if (!isStoredIdentityList(stored)) return;
      for (const [intent, id] of stored) this.identities.set(intent, id);
    } catch {
      // Privacy modes and managed browsers may deny storage. Request safety
      // still holds for the lifetime of this controller through its memory map.
    }
  }

  private persist(): void {
    if (!this.persistence) return;
    try {
      if (this.identities.size === 0) {
        this.persistence.storage.removeItem(this.persistence.key);
        return;
      }
      this.persistence.storage.setItem(this.persistence.key, JSON.stringify([...this.identities]));
    } catch {
      // Keep the in-memory identity when storage is unavailable or full.
    }
  }
}

const scopedRequestLifecycles = new Map<string, RequestLifecycleController>();

/** Route changes do not resolve an uncertain mutation, so controllers live at
 * session scope and are recovered when the user returns to the workspace. */
export function requestLifecycleForScope(scope: string): RequestLifecycleController {
  const existing = scopedRequestLifecycles.get(scope);
  if (existing) return existing;
  const storage = browserSessionStorage();
  const created = new RequestLifecycleController(
    undefined,
    storage ? { storage, key: `rivets-request-identities:${scope}` } : undefined,
  );
  scopedRequestLifecycles.set(scope, created);
  return created;
}

function isStoredIdentityList(value: unknown): value is Array<[string, string]> {
  return Array.isArray(value) && value.every((entry) =>
    Array.isArray(entry)
    && entry.length === 2
    && typeof entry[0] === "string"
    && entry[0].length > 0
    && typeof entry[1] === "string"
    && entry[1].length >= 8
    && entry[1].length <= 64,
  );
}

function browserSessionStorage(): RequestIdentityStorage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function shouldRetainRequestIdentity(error: unknown): boolean {
  if (!(error instanceof RivetsApiError)) return true;
  // Status 0 is the client's own deadline (`request_timeout`): the request
  // may have landed, so its retry must be the same request.
  return error.status === 0
    || error.status === 408
    || error.status === 425
    || error.status === 429
    || error.status >= 500
    || error.code === "operation_in_progress"
    || error.code === "pr_draft_state_failed"
    || error.code === "in_flight"
    || error.code === IDEMPOTENCY_OUTCOME_UNCERTAIN_CODE
    || error.code === WORKER_OUTCOME_UNKNOWN_CODE;
}
